import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { label: "Expertises", href: "#expertises" },
  { label: "Témoignages", href: "#temoignages" },
  { label: "Votre courtier", href: "/a-propos/" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass py-3" : "bg-transparent py-6"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 flex items-center justify-between">
        <a href="#top" data-testid="nav-brand-logo" className="flex items-baseline gap-2 group">
          <span className="font-serif text-2xl sm:text-3xl font-semibold tracking-tight text-ivory">
            ASSU<span className="text-amber inline-block" style={{ transform: "translateY(-0.1em)" }}>–</span>FRANCE
          </span>
          <span className="hidden xl:inline font-mono text-[10px] uppercase tracking-[0.25em] text-ivory/40 group-hover:text-amber transition-colors duration-300">
            Courtage
          </span>
        </a>

        <nav className="hidden xl:flex items-center gap-10" data-testid="nav-links">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace("é", "e")}`}
              className="font-mono text-sm uppercase tracking-[0.18em] text-ivory/75 hover:text-amber transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#devis"
            data-testid="nav-quote-cta"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-amber px-6 py-2.5 font-sans text-sm font-semibold text-obsidian transition-all duration-300 hover:bg-amber-warm hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] hover:-translate-y-0.5"
          >
            Devis gratuit
            <ArrowUpRight className="h-4 w-4" />
          </a>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="xl:hidden text-ivory p-2"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="xl:hidden glass border-t hairline overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-serif text-2xl text-ivory/80 hover:text-amber transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#devis"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-quote-cta"
                className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-amber px-6 py-3 text-sm font-semibold text-obsidian"
              >
                Devis gratuit <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
